import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { showToast } from '../components/Toast';
import './EditarHotspots.css';

const EditarHotspots = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recorrido, setRecorrido] = useState(null);
  const [imagenes, setImagenes] = useState([]);
  const [imagenActual, setImagenActual] = useState(0);
  const [hotspots, setHotspots] = useState([]);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [hotspotEditando, setHotspotEditando] = useState(null);
  const [hotspotMostrando, setHotspotMostrando] = useState(null);
  const [hotspotPosicion, setHotspotPosicion] = useState({ x: 0, y: 0 }); // Posición del clic para el modal
  const [marcadorTemporal, setMarcadorTemporal] = useState(null); // Marcador temporal que sigue el mouse
  const [marcadorTemporalPosicion, setMarcadorTemporalPosicion] = useState({ x: 0, y: 0, visible: false }); // Posición del marcador temporal en pantalla
  const viewerRef = useRef(null);
  const pannellumRef = useRef(null);
  const hotspotsRef = useRef([]);
  const hotspotsMapRef = useRef(new Map()); // Mapa para asociar IDs con datos de hotspots
  const marcadorTemporalRef = useRef(null); // Referencia al marcador temporal en Pannellum

  useEffect(() => {
    cargarRecorrido();
    cargarPannellum();
  }, []);

  useEffect(() => {
    const checkPannellum = setInterval(() => {
      if (window.pannellum && imagenes.length > 0) {
        clearInterval(checkPannellum);
        inicializarVisor();
      }
    }, 100);

    return () => clearInterval(checkPannellum);
  }, [imagenes, imagenActual]); // Removido modoEdicion para evitar reinicialización

  useEffect(() => {
    if (imagenActual >= 0 && imagenes[imagenActual]) {
      cargarHotspotsImagen(imagenes[imagenActual].id);
    }
  }, [imagenActual]);

  // Debug: Ver cuando cambia hotspotMostrando
  useEffect(() => {
    console.log('Estado hotspotMostrando cambió:', hotspotMostrando);
  }, [hotspotMostrando]);

  const cargarPannellum = () => {
    if (!document.querySelector('script[src*="pannellum"]')) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.js';
      script.async = true;
      document.head.appendChild(script);
    }

    if (!document.querySelector('link[href*="pannellum"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/pannellum@2.5.6/build/pannellum.css';
      document.head.appendChild(link);
    }
  };

  const cargarRecorrido = async () => {
    try {
      const response = await api.get(`/recorridos/${id}`);
      if (response.data.success) {
        setRecorrido(response.data.recorrido);
        const imagenesCargadas = response.data.recorrido.imagenes || [];
        setImagenes(imagenesCargadas);
        
        // Solo establecer imagenActual a 0 si no hay una imagen actual válida
        if (imagenesCargadas.length > 0 && imagenActual >= imagenesCargadas.length) {
          setImagenActual(0);
        } else if (imagenesCargadas.length > 0 && imagenActual < 0) {
          setImagenActual(0);
        }
        // Si imagenActual ya es válida, mantenerla
      }
    } catch (err) {
      console.error('Error al cargar recorrido:', err);
    }
  };

  const cargarHotspotsImagen = async (imagenId) => {
    try {
      const response = await api.get(`/hotspots/imagen/${imagenId}`);
      if (response.data.success) {
        console.log('=== HOTSPOTS RECIBIDOS DEL SERVIDOR ===');
        console.log('Total de hotspots:', response.data.hotspots?.length || 0);
        if (response.data.hotspots) {
          response.data.hotspots.forEach((h, idx) => {
            console.log(`Hotspot ${idx + 1}:`, {
              id: h.id,
              nombre: h.nombre,
              pitch: h.pitch,
              yaw: h.yaw,
              descripcion: h.descripcion,
              telefono: h.telefono,
              link: h.link,
              url_externa: h.url_externa,
              pitchType: typeof h.pitch,
              yawType: typeof h.yaw
            });
          });
        }
        setHotspots(response.data.hotspots || []);
        actualizarHotspotsEnVisor(response.data.hotspots || []);
      }
    } catch (err) {
      console.log('No hay hotspots para esta imagen');
    }
  };

  const inicializarVisor = () => {
    if (!window.pannellum || !viewerRef.current || imagenes.length === 0) return;

    const imagen = imagenes[imagenActual];
    if (!imagen || !imagen.ruta_archivo) return;

    // Guardar la posición actual del visor antes de destruirlo
    let savedPitch = 0;
    let savedYaw = 0;
    let savedHfov = 80;
    
    if (pannellumRef.current) {
      try {
        const config = pannellumRef.current.getConfig();
        savedPitch = config.pitch || 0;
        savedYaw = config.yaw || 0;
        savedHfov = config.hfov || 80;
        pannellumRef.current.destroy();
      } catch (e) {}
      pannellumRef.current = null;
    }

    const apiUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    const imagenUrl = `${apiUrl}${imagen.ruta_archivo}`;

    console.log('🖼️ [EditarHotspots] Construyendo URL de imagen:');
    console.log('  - apiUrl:', apiUrl);
    console.log('  - ruta_archivo:', imagen.ruta_archivo);
    console.log('  - imagenUrl completa:', imagenUrl);

    if (viewerRef.current) {
      viewerRef.current.innerHTML = '';
    }

    try {
      pannellumRef.current = window.pannellum.viewer(viewerRef.current, {
        type: 'equirectangular',
        panorama: imagenUrl,
        autoLoad: true,
        hfov: savedHfov,
        pitch: savedPitch,
        yaw: savedYaw,
        minHfov: 30,
        maxHfov: 120,
        showControls: true,
        compass: true,
        hotSpots: [],
        hotSpotDebug: true // Activar debug para ver coordenadas en consola
      });

      // Esperar a que el visor esté completamente cargado antes de permitir interacciones
      pannellumRef.current.on('load', () => {
        console.log('Visor Pannellum cargado completamente');
        const config = pannellumRef.current.getConfig();
        console.log('Configuración actual:', {
          pitch: config.pitch,
          yaw: config.yaw,
          hfov: config.hfov
        });
      });

      // Agregar listener para clics en hotspots informativos
      pannellumRef.current.on('hotspotclick', (event, hotspot) => {
        console.log('Evento hotspotclick disparado:', hotspot);
        event.stopPropagation();
        event.preventDefault();
        
        // Solo manejar hotspots informativos (no de navegación)
        if (hotspot && hotspot.type === 'info' && hotspot.id) {
          // Extraer el ID del hotspot del formato "hotspot-{id}"
          const hotspotId = parseInt(hotspot.id.replace('hotspot-', ''));
          console.log('Buscando hotspot con ID:', hotspotId);
          
          // Buscar el elemento DOM del hotspot para obtener su posición
          const hotspotElement = viewerRef.current?.querySelector(`[data-hotspot-id="${hotspotId}"]`);
          let x = window.innerWidth / 2;
          let y = window.innerHeight / 2;
          
          if (hotspotElement) {
            const rect = hotspotElement.getBoundingClientRect();
            x = rect.left + rect.width / 2;
            y = rect.top;
          } else {
            // Si no encontramos el elemento, usar las coordenadas del evento
            if (event.clientX && event.clientY) {
              x = event.clientX;
              y = event.clientY;
            }
          }
          
          // Buscar en el mapa primero
          const hotspotCompleto = hotspotsMapRef.current.get(hotspotId) || 
                                  hotspotsRef.current.find(h => h.id === hotspotId);
          
          console.log('Hotspot encontrado:', hotspotCompleto);
          if (hotspotCompleto) {
            console.log('Mostrando modal para:', hotspotCompleto.nombre, 'en posición:', { x, y });
            setHotspotPosicion({ x, y });
            setHotspotMostrando(hotspotCompleto);
          } else {
            console.error('No se encontró el hotspot con ID:', hotspotId);
            console.log('Hotspots disponibles:', hotspotsRef.current);
          }
        }
      });

      // Cargar hotspots existentes
      if (imagenes[imagenActual] && imagenes[imagenActual].id) {
        setTimeout(() => {
          cargarHotspotsImagen(imagenes[imagenActual].id);
        }, 500);
      }
    } catch (err) {
      console.error('Error al inicializar visor:', err);
    }
  };

  // Efecto para manejar movimiento del mouse y mostrar marcador temporal
  useEffect(() => {
    // Solo mostrar marcador temporal si estamos en modo edición y NO hay formulario abierto
    if (!modoEdicion || hotspotEditando !== null || !pannellumRef.current) return;

    const viewerElement = viewerRef.current;
    if (!viewerElement) return;

    const pannellumInstance = pannellumRef.current;

    const mouseMoveHandler = (e) => {
      // No procesar si el mouse está sobre controles o modales
      if (e.target.closest('.pnlm-controls') || 
          e.target.closest('.modal-hotspot') ||
          e.target.closest('button')) {
        // Ocultar marcador temporal visual
        setMarcadorTemporalPosicion({ x: 0, y: 0, visible: false });
        // Remover marcador temporal si existe
        if (marcadorTemporalRef.current) {
          try {
            const TEMP_HOTSPOT_ID = 'temp-marker-preview';
            pannellumInstance.removeHotSpot(TEMP_HOTSPOT_ID);
          } catch (err) {
            if (marcadorTemporalRef.current.index !== undefined) {
              try {
                pannellumInstance.removeHotSpot(marcadorTemporalRef.current.index);
              } catch (e) {}
            }
          }
          marcadorTemporalRef.current = null;
        }
        return;
      }
      
      // Actualizar posición visual del marcador temporal
      const canvas = viewerElement.querySelector('canvas');
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setMarcadorTemporalPosicion({ 
          x: e.clientX, 
          y: e.clientY, 
          visible: true 
        });
      }

      try {
        // Obtener el canvas del viewer
        const canvas = viewerElement.querySelector('canvas');
        if (!canvas) return;
        
        // Obtener posición del mouse relativa al canvas
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Obtener dimensiones del canvas
        const width = rect.width;
        const height = rect.height;
        
        // Obtener configuración actual del viewer
        const config = pannellumInstance.getConfig();
        const currentPitch = config.pitch || 0;
        const currentYaw = config.yaw || 0;
        const currentHfov = config.hfov || 80;
        
        // Normalizar coordenadas del mouse (0 a 1)
        const normalizedX = (x / width) * 2 - 1; // -1 a 1 (izquierda a derecha)
        const normalizedY = (y / height) * 2 - 1; // -1 a 1 (arriba a abajo)
        
        // Calcular pitch y yaw basado en la posición del mouse y la vista actual
        // Fórmula basada en la proyección equirectangular de Pannellum
        const hfovRad = (currentHfov * Math.PI) / 180;
        
        // Calcular yaw: basado en la posición horizontal del mouse
        const yaw = currentYaw + (normalizedX * hfovRad / 2) * (180 / Math.PI);
        
        // Calcular pitch: basado en la posición vertical del mouse
        // El pitch máximo visible es aproximadamente hfov/2 en vertical
        const vfov = hfovRad * (height / width); // Campo de visión vertical aproximado
        const pitch = currentPitch - (normalizedY * vfov / 2) * (180 / Math.PI);
        
        // Limitar pitch a -90 a 90 (límites físicos)
        const clampedPitch = Math.max(-90, Math.min(90, pitch));
        
        // Normalizar yaw a -180 a 180
        let normalizedYaw = yaw;
        while (normalizedYaw > 180) normalizedYaw -= 360;
        while (normalizedYaw < -180) normalizedYaw += 360;
        
        // Verificar que las coordenadas sean diferentes a las anteriores (con tolerancia)
        const lastCoords = marcadorTemporalRef.current?.lastCoords || { pitch: null, yaw: null };
        const tolerance = 0.1; // Tolerancia de 0.1 grados
        if (lastCoords.pitch !== null && 
            Math.abs(lastCoords.pitch - clampedPitch) < tolerance &&
            Math.abs(lastCoords.yaw - normalizedYaw) < tolerance) {
          return; // No actualizar si las coordenadas son muy similares
        }
        
        console.log('Coordenadas calculadas manualmente:', { 
          x, y, 
          normalizedX, normalizedY,
          currentPitch, currentYaw, currentHfov,
          pitch: clampedPitch, 
          yaw: normalizedYaw 
        });
        
        // Usar un ID único fijo para el hotspot temporal
        const TEMP_HOTSPOT_ID = 'temp-marker-preview';
        
        // Remover marcador temporal anterior si existe
        if (marcadorTemporalRef.current !== null) {
          try {
            // Intentar remover por ID primero
            pannellumInstance.removeHotSpot(TEMP_HOTSPOT_ID);
          } catch (err) {
            // Si falla, intentar remover por índice
            if (marcadorTemporalRef.current.index !== undefined) {
              try {
                pannellumInstance.removeHotSpot(marcadorTemporalRef.current.index);
              } catch (e) {
                console.warn('Error al remover marcador temporal:', e);
              }
            }
          }
        }
        
        // Crear marcador temporal que sigue el mouse con ID único
        try {
          const hotspotConfig = {
            id: TEMP_HOTSPOT_ID,
            pitch: clampedPitch,
            yaw: normalizedYaw,
            type: 'info',
            text: 'Clic aquí para agregar punto',
            cssClass: 'hotspot-temporal-preview'
          };
          
          const index = pannellumInstance.addHotSpot(hotspotConfig);
          
          // Guardar referencia con las coordenadas
          marcadorTemporalRef.current = {
            index: index,
            id: TEMP_HOTSPOT_ID,
            pitch: clampedPitch,
            yaw: normalizedYaw,
            lastCoords: { pitch: clampedPitch, yaw: normalizedYaw }
          };
          
          console.log('Marcador temporal creado/actualizado:', marcadorTemporalRef.current);
        } catch (err) {
          console.error('Error al crear marcador temporal:', err);
        }
      } catch (err) {
        console.error('Error en mouseMoveHandler:', err);
      }
    };

    const clickHandler = (e) => {
      // No procesar si el clic es en controles, modales o botones
      if (e.target.tagName === 'INPUT' || 
          e.target.tagName === 'TEXTAREA' || 
          e.target.tagName === 'SELECT' ||
          e.target.closest('button') || 
          e.target.closest('.modal-hotspot') ||
          e.target.closest('.modal-content') ||
          e.target.closest('.pnlm-controls')) {
        return;
      }

      // Si el clic es en un hotspot existente, no procesar
      if (e.target.closest('.pnlm-hotspot-base') && !e.target.closest('.hotspot-temporal-preview')) {
        return;
      }

      // Solo procesar clics si estamos en modo edición, hay instancia de Pannellum, 
      // y NO hay un formulario abierto (hotspotEditando debe ser null)
      if (!modoEdicion || !pannellumInstance || hotspotEditando !== null) {
        return;
      }

      try {
        console.log('=== CLIC EN IMAGEN - CAPTURANDO COORDENADAS ===');
        console.log('Evento completo:', e);
        console.log('Target:', e.target);
        console.log('ClientX/Y:', e.clientX, e.clientY);
        
        // Calcular coordenadas directamente del clic usando el mismo método que mouseMoveHandler
        let yaw, pitch;
        
        // Obtener el canvas del viewer
        const canvas = viewerElement.querySelector('canvas');
        if (!canvas) {
          console.error('No se encontró el canvas');
          return;
        }
        
        // Obtener posición del clic relativa al canvas
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Obtener dimensiones del canvas
        const width = rect.width;
        const height = rect.height;
        
        // Obtener configuración actual del viewer
        const config = pannellumInstance.getConfig();
        const currentPitch = config.pitch || 0;
        const currentYaw = config.yaw || 0;
        const currentHfov = config.hfov || 80;
        
        // Normalizar coordenadas del clic (0 a 1)
        const normalizedX = (x / width) * 2 - 1; // -1 a 1 (izquierda a derecha)
        const normalizedY = (y / height) * 2 - 1; // -1 a 1 (arriba a abajo)
        
        // Calcular pitch y yaw basado en la posición del clic y la vista actual
        // Fórmula basada en la proyección equirectangular de Pannellum
        const hfovRad = (currentHfov * Math.PI) / 180;
        
        // Calcular yaw: basado en la posición horizontal del clic
        yaw = currentYaw + (normalizedX * hfovRad / 2) * (180 / Math.PI);
        
        // Calcular pitch: basado en la posición vertical del clic
        const vfov = hfovRad * (height / width); // Campo de visión vertical aproximado
        pitch = currentPitch - (normalizedY * vfov / 2) * (180 / Math.PI);
        
        // Limitar pitch a -90 a 90 (límites físicos)
        pitch = Math.max(-90, Math.min(90, pitch));
        
        // Normalizar yaw a -180 a 180
        while (yaw > 180) yaw -= 360;
        while (yaw < -180) yaw += 360;
        
        console.log('Coordenadas calculadas del clic:', { 
          x, y, 
          normalizedX, normalizedY,
          currentPitch, currentYaw, currentHfov,
          pitch, 
          yaw 
        });
        
        console.log('Coordenadas finales obtenidas:', { yaw, pitch });
        
        // Validar coordenadas
        if (typeof yaw === 'number' && typeof pitch === 'number' && 
            !isNaN(yaw) && !isNaN(pitch) && isFinite(yaw) && isFinite(pitch)) {
          
          console.log('✅ Coordenadas válidas:', { pitch, yaw });
          
          // Remover marcador temporal
          if (marcadorTemporalRef.current && marcadorTemporalRef.current.index !== undefined) {
            try {
              pannellumInstance.removeHotSpot(marcadorTemporalRef.current.index);
              marcadorTemporalRef.current = null;
            } catch (err) {
              console.error('Error al remover marcador temporal:', err);
            }
          }
          
          e.preventDefault();
          e.stopPropagation();
          
          agregarHotspot(yaw, pitch);
        } else {
          console.error('❌ Coordenadas inválidas:', { yaw, pitch });
          console.error('Tipo de yaw:', typeof yaw, 'Tipo de pitch:', typeof pitch);
          showToast('Error: No se pudieron obtener las coordenadas válidas. Intenta hacer clic nuevamente.', 'error');
        }
      } catch (err) {
        console.error('Error completo al obtener coordenadas:', err);
        console.error('Stack trace:', err.stack);
        showToast('Error al obtener las coordenadas del punto.', 'error');
      }
    };

    // Agregar listeners
    viewerElement.addEventListener('mousemove', mouseMoveHandler);
    viewerElement.addEventListener('click', clickHandler, true);
    
    console.log('✅ Listeners de mouse agregados para modo edición');

    return () => {
      viewerElement.removeEventListener('mousemove', mouseMoveHandler);
      viewerElement.removeEventListener('click', clickHandler, true);
      
      // Ocultar marcador temporal visual
      setMarcadorTemporalPosicion({ x: 0, y: 0, visible: false });
      
      // Remover marcador temporal al salir del modo edición
      if (marcadorTemporalRef.current && pannellumInstance) {
        try {
          const TEMP_HOTSPOT_ID = 'temp-marker-preview';
          pannellumInstance.removeHotSpot(TEMP_HOTSPOT_ID);
        } catch (err) {
          if (marcadorTemporalRef.current.index !== undefined) {
            try {
              pannellumInstance.removeHotSpot(marcadorTemporalRef.current.index);
            } catch (e) {}
          }
        }
        marcadorTemporalRef.current = null;
      }
      
      console.log('✅ Listeners de mouse removidos');
    };
  }, [modoEdicion, hotspotEditando]);

  const actualizarHotspotsEnVisor = async (hotspotsList) => {
    if (!pannellumRef.current || !window.pannellum) return;

    try {
      // Guardar referencia a la lista de hotspots para el evento de clic
      hotspotsRef.current = hotspotsList;
      
      // Actualizar el mapa de hotspots
      hotspotsMapRef.current.clear();
      hotspotsList.forEach(h => hotspotsMapRef.current.set(h.id, h));
      
      setHotspots(hotspotsList);

      // Limpiar TODOS los hotspots existentes de forma más agresiva
      const TEMP_HOTSPOT_ID = 'temp-marker-preview';
      let attempts = 0;
      const maxAttempts = 10;
      
      // Remover todos los marcadores temporales primero
      while (attempts < maxAttempts) {
        const config = pannellumRef.current.getConfig();
        const tempHotspots = config.hotSpots?.filter(hs => 
          hs.id && hs.id.includes(TEMP_HOTSPOT_ID)
        ) || [];
        
        if (tempHotspots.length === 0) break;
        
        console.log(`Removiendo ${tempHotspots.length} marcadores temporales...`);
        for (const tempHs of tempHotspots) {
          try {
            if (tempHs.id) {
              pannellumRef.current.removeHotSpot(tempHs.id);
            }
          } catch (e) {
            // Intentar por índice si falla por ID
            const index = config.hotSpots.indexOf(tempHs);
            if (index !== -1) {
              try {
                pannellumRef.current.removeHotSpot(index);
              } catch (e2) {}
            }
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, 50));
        attempts++;
      }
      
      // Ahora remover todos los hotspots permanentes
      attempts = 0;
      while (attempts < maxAttempts) {
        const config = pannellumRef.current.getConfig();
        const permanentHotspots = config.hotSpots?.filter(hs => 
          !hs.id || !hs.id.includes(TEMP_HOTSPOT_ID)
        ) || [];
        
        if (permanentHotspots.length === 0) break;
        
        console.log(`Limpiando ${permanentHotspots.length} hotspots permanentes...`);
        
        // Remover en orden inverso por índice
        for (let i = config.hotSpots.length - 1; i >= 0; i--) {
          const hs = config.hotSpots[i];
          if (!hs.id || !hs.id.includes(TEMP_HOTSPOT_ID)) {
            try {
              // Intentar por ID primero
              if (hs.id) {
                pannellumRef.current.removeHotSpot(hs.id);
              } else {
                // Si no tiene ID, remover por índice
                pannellumRef.current.removeHotSpot(i);
              }
            } catch (e) {
              // Si falla, intentar por índice
              try {
                pannellumRef.current.removeHotSpot(i);
              } catch (e2) {}
            }
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      // Verificación final
      const finalConfig = pannellumRef.current.getConfig();
      const finalHotspots = finalConfig.hotSpots?.length || 0;
      console.log(`Hotspots restantes después de limpiar: ${finalHotspots}`);
      
      if (finalHotspots > 0) {
        console.warn(`⚠️ Aún quedan ${finalHotspots} hotspots después de limpiar. Intentando limpieza forzada...`);
        // Limpieza forzada: remover todos los hotspots restantes
        const remaining = finalConfig.hotSpots || [];
        for (let i = remaining.length - 1; i >= 0; i--) {
          try {
            pannellumRef.current.removeHotSpot(i);
          } catch (e) {
            if (remaining[i].id) {
              try {
                pannellumRef.current.removeHotSpot(remaining[i].id);
              } catch (e2) {}
            }
          }
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Limpiar referencia del marcador temporal
      marcadorTemporalRef.current = null;

      // Esperar un momento para asegurar que la limpieza se complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Guardar hotspots en el mapa para acceso rápido
      hotspotsMapRef.current.clear();
      hotspotsList.forEach(h => hotspotsMapRef.current.set(h.id, h));
      
      // Agregar hotspots uno por uno con un pequeño delay entre cada uno
      for (const hotspot of hotspotsList) {
        try {
          const destinoIndex = imagenes.findIndex(img => img.id === hotspot.imagen_destino_id);
          
          // Asegurar que las coordenadas sean números válidos
          const pitch = parseFloat(hotspot.pitch);
          const yaw = parseFloat(hotspot.yaw);
          
          if (isNaN(pitch) || isNaN(yaw)) {
            console.error('❌ Coordenadas inválidas para hotspot:', hotspot);
            console.error('Valores raw:', { pitch: hotspot.pitch, yaw: hotspot.yaw });
            return;
          }
          
          // Validar que las coordenadas estén en rangos válidos para Pannellum
          // pitch: -90 a 90 (grados)
          // yaw: -180 a 180 (grados)
          if (pitch < -90 || pitch > 90 || yaw < -180 || yaw > 180) {
            console.warn('⚠️ Coordenadas fuera de rango normal:', { pitch, yaw });
            // No retornar, solo advertir - Pannellum puede manejar valores fuera de rango
          }
          
          console.log(`✅ Creando hotspot ${hotspot.id} "${hotspot.nombre}" en coordenadas: pitch=${pitch}, yaw=${yaw}`);
          console.log(`   Coordenadas raw del hotspot:`, { pitch: hotspot.pitch, yaw: hotspot.yaw });
          console.log(`   Coordenadas parseadas:`, { pitch, yaw });
          
          const hotspotConfig = {
            pitch: pitch,
            yaw: yaw,
            type: hotspot.tipo === 'navegacion' ? 'scene' : 'info',
            text: hotspot.nombre || 'Punto de interés',
            cssClass: 'custom-hotspot-editable',
            id: `hotspot-${hotspot.id}` // ID único para identificar el hotspot
          };
          
          console.log(`   Configuración del hotspot para Pannellum:`, hotspotConfig);

          if (hotspot.tipo === 'navegacion' && destinoIndex !== -1) {
            hotspotConfig.sceneId = `imagen_${destinoIndex}`;
            hotspotConfig.targetPitch = 0;
            hotspotConfig.targetYaw = 0;
            hotspotConfig.targetHfov = 80;
          }

          // Agregar hotspot con las coordenadas exactas
          console.log(`Agregando hotspot ${hotspot.id} con configuración:`, hotspotConfig);
          const hotspotIndex = pannellumRef.current.addHotSpot(hotspotConfig);
          console.log(`Hotspot agregado, índice devuelto:`, hotspotIndex);
          
          // Verificar inmediatamente después de agregar
          setTimeout(() => {
            const configAfter = pannellumRef.current.getConfig();
            const allHotspots = configAfter.hotSpots || [];
            console.log(`Total de hotspots en visor después de agregar ${hotspot.id}:`, allHotspots.length);
            
            // Buscar el hotspot recién agregado
            const addedHotspot = allHotspots.find(hs => 
              hs.id === hotspotConfig.id
            );
            
            if (addedHotspot) {
              const addedPitch = parseFloat(addedHotspot.pitch);
              const addedYaw = parseFloat(addedHotspot.yaw);
              const pitchDiff = Math.abs(addedPitch - pitch);
              const yawDiff = Math.abs(addedYaw - yaw);
              
              console.log(`✅ Hotspot ${hotspot.id} encontrado en configuración:`, {
                pitchEsperado: pitch,
                yawEsperado: yaw,
                pitchEncontrado: addedPitch,
                yawEncontrado: addedYaw,
                diferenciaPitch: pitchDiff,
                diferenciaYaw: yawDiff,
                id: addedHotspot.id
              });
              
              if (pitchDiff > 0.1 || yawDiff > 0.1) {
                console.error(`❌ ERROR: Hotspot ${hotspot.id} tiene coordenadas incorrectas!`);
                console.error(`   Esperado: pitch=${pitch}, yaw=${yaw}`);
                console.error(`   Encontrado: pitch=${addedPitch}, yaw=${addedYaw}`);
              }
            } else {
              console.warn(`⚠️ Hotspot ${hotspot.id} no encontrado después de agregar`);
              console.warn(`   Hotspots disponibles:`, allHotspots.map(hs => ({ id: hs.id, pitch: hs.pitch, yaw: hs.yaw })));
            }
          }, 300);
          
          // Pequeño delay entre hotspots
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (e) {
          console.error('Error al agregar hotspot:', e);
        }
      }

      // Forzar actualización del visor después de agregar todos los hotspots
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verificar que los hotspots se agregaron correctamente
      const verificationConfig = pannellumRef.current.getConfig();
      const verificationHotspots = verificationConfig.hotSpots || [];
      console.log(`✅ Hotspots finales en visor: ${verificationHotspots.length}`);
      verificationHotspots.forEach((hs, idx) => {
        console.log(`  Hotspot ${idx + 1}:`, {
          id: hs.id,
          pitch: hs.pitch,
          yaw: hs.yaw,
          text: hs.text
        });
      });

      // Aplicar estilos y event listeners a los hotspots después de crearlos
      setTimeout(() => {
        if (viewerRef.current && pannellumRef.current) {
          // Obtener la configuración actual para mapear hotspots correctamente
          const config = pannellumRef.current.getConfig();
          const hotspotsConfig = config.hotSpots || [];
          
          hotspotsConfig.forEach((hotspotConfig, index) => {
            // Buscar el hotspot correspondiente por ID
            if (hotspotConfig.id) {
              const hotspotId = parseInt(hotspotConfig.id.replace('hotspot-', ''));
              const hotspot = hotspotsList.find(h => h.id === hotspotId);
              
              if (hotspot) {
                // Buscar el elemento DOM del hotspot
                const hotspotElements = viewerRef.current.querySelectorAll('.pnlm-hotspot-base');
                if (hotspotElements[index]) {
                  const element = hotspotElements[index];
                  
                  // Aplicar estilos inline para asegurar visibilidad
                  // IMPORTANTE: NO modificar transform, left, top - Pannellum los usa para posicionamiento
                  element.style.backgroundColor = 'rgba(255, 0, 0, 0.95)';
                  element.style.border = '4px solid #ffffff';
                  element.style.borderRadius = '50%';
                  element.style.width = '45px';
                  element.style.height = '45px';
                  element.style.cursor = 'pointer';
                  element.style.boxShadow = '0 0 10px rgba(255, 0, 0, 0.8), 0 0 20px rgba(255, 0, 0, 0.5)';
                  element.style.transition = 'background-color 0.3s ease, box-shadow 0.3s ease';
                  element.style.opacity = '1';
                  element.style.visibility = 'visible';
                  element.style.display = 'block';
                  // NO modificar position, transform, left, top - Pannellum los necesita para posicionar correctamente
                  
                  // Crear punto central blanco
                  if (!element.querySelector('.hotspot-center')) {
                    const centerDot = document.createElement('div');
                    centerDot.className = 'hotspot-center';
                    centerDot.style.position = 'absolute';
                    centerDot.style.top = '50%';
                    centerDot.style.left = '50%';
                    centerDot.style.transform = 'translate(-50%, -50%)';
                    centerDot.style.width = '12px';
                    centerDot.style.height = '12px';
                    centerDot.style.backgroundColor = '#ffffff';
                    centerDot.style.borderRadius = '50%';
                    centerDot.style.boxShadow = '0 0 5px rgba(0, 0, 0, 0.3)';
                    centerDot.style.zIndex = '1';
                    centerDot.style.pointerEvents = 'none';
                    element.appendChild(centerDot);
                  }
                  
                  // Efecto hover SIN transform para evitar movimiento
                  const handleMouseEnter = () => {
                    element.style.backgroundColor = 'rgba(255, 0, 0, 1)';
                    element.style.boxShadow = '0 0 15px rgba(255, 0, 0, 1), 0 0 30px rgba(255, 0, 0, 0.7)';
                    // NO usar transform: scale() porque puede causar movimiento
                  };
                  
                  const handleMouseLeave = () => {
                    element.style.backgroundColor = 'rgba(255, 0, 0, 0.95)';
                    element.style.boxShadow = '0 0 10px rgba(255, 0, 0, 0.8), 0 0 20px rgba(255, 0, 0, 0.5)';
                  };
                  
                  // Remover listeners anteriores
                  if (element._mouseEnterHandler) {
                    element.removeEventListener('mouseenter', element._mouseEnterHandler);
                    element.removeEventListener('mouseleave', element._mouseLeaveHandler);
                  }
                  
                  element._mouseEnterHandler = handleMouseEnter;
                  element._mouseLeaveHandler = handleMouseLeave;
                  element.addEventListener('mouseenter', handleMouseEnter);
                  element.addEventListener('mouseleave', handleMouseLeave);
                  
                  // Agregar event listener para hotspots informativos
                  if (hotspot.tipo === 'informativo' || hotspot.tipo === 'info') {
                    // Función para manejar el clic - más robusta
                    const handleClick = (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      
                      console.log('=== CLICK EN HOTSPOT ===');
                      console.log('Hotspot ID:', hotspot.id);
                      console.log('Hotspot nombre:', hotspot.nombre);
                      
                      // Obtener la posición del elemento en la pantalla
                      const rect = element.getBoundingClientRect();
                      const x = rect.left + rect.width / 2; // Centro horizontal del hotspot
                      const y = rect.top; // Parte superior del hotspot
                      
                      console.log('Posición del hotspot:', { x, y });
                      
                      // Guardar la posición para el modal
                      setHotspotPosicion({ x, y });
                      
                      // Obtener el hotspot actualizado del mapa
                      const hotspotActual = hotspotsMapRef.current.get(hotspot.id) || hotspot;
                      
                      // Forzar actualización del estado
                      setHotspotMostrando({...hotspotActual});
                      
                      return false;
                    };
                    
                    // Remover listeners anteriores si existen
                    if (element._hotspotClickHandler) {
                      element.removeEventListener('click', element._hotspotClickHandler);
                      element.removeEventListener('mousedown', element._hotspotClickHandler);
                      element.removeEventListener('mouseup', element._hotspotClickHandler);
                    }
                    
                    // Agregar múltiples listeners para asegurar captura
                    element._hotspotClickHandler = handleClick;
                    
                    // Usar capture phase y también bubble phase
                    element.addEventListener('click', handleClick, true);
                    element.addEventListener('click', handleClick, false);
                    element.addEventListener('mousedown', handleClick, true);
                    element.addEventListener('mouseup', handleClick, true);
                    
                    // Marcar el elemento para debugging
                    element.setAttribute('data-hotspot-id', hotspot.id);
                    element.setAttribute('data-hotspot-nombre', hotspot.nombre);
                    element.setAttribute('data-hotspot-tipo', hotspot.tipo);
                    
                    // Agregar estilo para asegurar que sea clickeable
                    element.style.pointerEvents = 'auto';
                    element.style.zIndex = '1000';
                  }
                }
              }
            }
          });
        }
      }, 500);
    } catch (e) {
      console.error('Error al actualizar hotspots:', e);
    }
  };

  const agregarHotspot = (yaw, pitch) => {
    console.log('=== AGREGAR HOTSPOT LLAMADO ===');
    console.log('Coordenadas recibidas:', { yaw, pitch });
    console.log('Imagen actual:', imagenActual);
    console.log('ID de imagen:', imagenes[imagenActual]?.id);
    
    if (!imagenes[imagenActual] || !imagenes[imagenActual].id) {
      console.error('No hay imagen actual válida');
      showToast('Error: No hay imagen seleccionada', 'error');
      return;
    }
    
    const nuevoHotspot = {
      imagen_id: imagenes[imagenActual].id,
      yaw: yaw,
      pitch: pitch,
      nombre: '',
      tipo: 'info',
      descripcion: '',
      imagen_destino_id: null,
      url_externa: null,
      telefono: '',
      link: ''
    };
    
    console.log('Nuevo hotspot a crear:', nuevoHotspot);
    setHotspotEditando(nuevoHotspot);
    console.log('Estado hotspotEditando actualizado');
  };

  const guardarHotspot = async () => {
    if (!hotspotEditando || !hotspotEditando.nombre || !hotspotEditando.nombre.trim()) {
      showToast('Por favor, ingresa un nombre para el punto de interés', 'warning');
      return;
    }

    // Validar que si es tipo navegación, tenga imagen destino
    if (hotspotEditando.tipo === 'navegacion' && !hotspotEditando.imagen_destino_id) {
      showToast('Por favor, selecciona una imagen destino para el punto de navegación', 'warning');
      return;
    }

    // Validar que pitch y yaw sean números válidos
    const pitch = parseFloat(hotspotEditando.pitch);
    const yaw = parseFloat(hotspotEditando.yaw);
    
    if (isNaN(pitch) || isNaN(yaw)) {
      showToast('Error: Las coordenadas del punto no son válidas. Por favor, haz clic nuevamente en la imagen.', 'error');
      console.error('Coordenadas inválidas:', { pitch: hotspotEditando.pitch, yaw: hotspotEditando.yaw });
      return;
    }

    try {
      // Preparar valores de teléfono y link
      const telefonoValue = hotspotEditando.telefono && hotspotEditando.telefono.toString().trim() 
        ? hotspotEditando.telefono.toString().trim() 
        : null;
      const linkValue = hotspotEditando.link && hotspotEditando.link.toString().trim() 
        ? hotspotEditando.link.toString().trim() 
        : null;
      
      console.log('Valores antes de enviar:', {
        telefonoRaw: hotspotEditando.telefono,
        telefonoValue: telefonoValue,
        linkRaw: hotspotEditando.link,
        linkValue: linkValue
      });
      
      const datosHotspot = {
        imagen_id: hotspotEditando.imagen_id,
        nombre: hotspotEditando.nombre.trim(),
        descripcion: hotspotEditando.descripcion ? hotspotEditando.descripcion.trim() : null,
        pitch: pitch,
        yaw: yaw,
        tipo: hotspotEditando.tipo === 'info' ? 'informativo' : 'navegacion',
        imagen_destino_id: hotspotEditando.tipo === 'navegacion' ? hotspotEditando.imagen_destino_id : null,
        url_externa: hotspotEditando.url_externa || null,
        telefono: telefonoValue,
        link: linkValue
      };

      console.log('📤 Enviando hotspot completo:', JSON.stringify(datosHotspot, null, 2));
      console.log('📤 Estado hotspotEditando:', hotspotEditando);
      console.log('📤 Coordenadas que se están enviando:', { pitch, yaw });
      console.log('📤 Campos adicionales:', { 
        telefono: datosHotspot.telefono, 
        link: datosHotspot.link,
        telefonoType: typeof datosHotspot.telefono,
        linkType: typeof datosHotspot.link
      });

      // Determinar si es edición o creación
      const esEdicion = hotspotEditando.id !== undefined && hotspotEditando.id !== null;
      console.log('📤 Es edición?', esEdicion, 'ID:', hotspotEditando.id);
      
      let response;
      
      if (esEdicion) {
        // Actualizar hotspot existente
        console.log('📤 Enviando PUT a /hotspots/' + hotspotEditando.id);
        console.log('📤 Payload completo:', JSON.stringify(datosHotspot, null, 2));
        response = await api.put(`/hotspots/${hotspotEditando.id}`, datosHotspot);
        console.log('✅ Respuesta del PUT:', JSON.stringify(response.data, null, 2));
      } else {
        // Crear nuevo hotspot
        console.log('📤 Enviando POST a /hotspots');
        response = await api.post('/hotspots', datosHotspot);
        console.log('✅ Respuesta del POST:', response.data);
      }

      if (response.data.success) {
        showToast(esEdicion ? 'Punto de interés actualizado exitosamente' : 'Punto de interés agregado exitosamente', 'success');
        
        // Guardar la posición actual del visor antes de recargar hotspots
        let currentPitch = 0;
        let currentYaw = 0;
        let currentHfov = 80;
        
        if (pannellumRef.current) {
          try {
            const config = pannellumRef.current.getConfig();
            currentPitch = config.pitch || 0;
            currentYaw = config.yaw || 0;
            currentHfov = config.hfov || 80;
          } catch (e) {
            console.error('Error al obtener configuración del visor:', e);
          }
        }
        
        // Recargar hotspots sin reinicializar el visor
        // Esperar un momento antes de recargar para asegurar que el servidor haya procesado
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Recargar hotspots y verificar respuesta
        try {
          const reloadResponse = await api.get(`/hotspots/imagen/${imagenes[imagenActual].id}`);
          if (reloadResponse.data.success && esEdicion) {
            const hotspotActualizado = reloadResponse.data.hotspots.find(h => h.id === hotspotEditando.id);
            if (hotspotActualizado) {
              console.log('✅ Hotspot actualizado recibido del servidor:', {
                id: hotspotActualizado.id,
                nombre: hotspotActualizado.nombre,
                telefono: hotspotActualizado.telefono,
                link: hotspotActualizado.link,
                descripcion: hotspotActualizado.descripcion
              });
              
              // Verificar si los campos se guardaron
              if (telefonoValue && !hotspotActualizado.telefono) {
                console.error('❌ ERROR: El teléfono no se guardó en el servidor');
              }
              if (linkValue && !hotspotActualizado.link) {
                console.error('❌ ERROR: El link no se guardó en el servidor');
              }
            }
          }
        } catch (reloadErr) {
          console.error('Error al recargar hotspots para verificación:', reloadErr);
        }
        
        await cargarHotspotsImagen(imagenes[imagenActual].id);
        
        // Restaurar la posición del visor después de recargar hotspots
        if (pannellumRef.current) {
          try {
            // Esperar un momento más para que los hotspots se rendericen
            await new Promise(resolve => setTimeout(resolve, 200));
            pannellumRef.current.lookAt(currentPitch, currentYaw, currentHfov);
            
            // Verificar que los hotspots se agregaron correctamente
            setTimeout(() => {
              const config = pannellumRef.current.getConfig();
              console.log('Hotspots en el visor después de recargar:', config.hotSpots?.length || 0);
              if (config.hotSpots) {
                config.hotSpots.forEach((hs, idx) => {
                  console.log(`Hotspot ${idx}:`, {
                    id: hs.id,
                    pitch: hs.pitch,
                    yaw: hs.yaw,
                    text: hs.text
                  });
                });
              }
            }, 500);
          } catch (e) {
            console.error('Error al restaurar posición del visor:', e);
          }
        }
        
        // Remover marcador temporal si existe
        if (marcadorTemporalRef.current && pannellumRef.current) {
          try {
            const TEMP_HOTSPOT_ID = 'temp-marker-preview';
            pannellumRef.current.removeHotSpot(TEMP_HOTSPOT_ID);
          } catch (err) {
            if (marcadorTemporalRef.current.index !== undefined) {
              try {
                pannellumRef.current.removeHotSpot(marcadorTemporalRef.current.index);
              } catch (e) {}
            }
          }
          marcadorTemporalRef.current = null;
        }
        
        setHotspotEditando(null);
        // NO cambiar modoEdicion automáticamente para permitir agregar más hotspots
        // setModoEdicion(false);
        
        // Limpiar cualquier marcador temporal que pueda quedar
        if (marcadorTemporalRef.current !== null && pannellumRef.current) {
          try {
            const TEMP_HOTSPOT_ID = 'temp-marker-preview';
            pannellumRef.current.removeHotSpot(TEMP_HOTSPOT_ID);
          } catch (err) {
            if (marcadorTemporalRef.current.index !== undefined) {
              try {
                pannellumRef.current.removeHotSpot(marcadorTemporalRef.current.index);
              } catch (e) {}
            }
          }
          marcadorTemporalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error completo:', err);
      console.error('Respuesta del error:', err.response?.data);
      
      let errorMsg = 'Error al guardar punto de interés';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        errorMsg = errorData.message || errorMsg;
        
        // Si el error menciona que la tabla no existe
        if (errorMsg.includes('tabla') || errorMsg.includes('table') || errorMsg.includes('no existe') || errorMsg.includes('ER_NO_SUCH_TABLE')) {
          errorMsg = 'La tabla hotspots no existe. Ejecuta: database/hotspots.sql en MySQL';
        }
        
        // Mostrar detalles del error en desarrollo
        if (process.env.NODE_ENV === 'development' && errorData.error) {
          console.error('Detalles del error:', errorData.error);
          if (errorData.error.sqlMessage) {
            errorMsg += ` - ${errorData.error.sqlMessage}`;
          }
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      // Si no hay conexión con el servidor
      if (!err.response) {
        errorMsg = 'No se pudo conectar con el servidor. Verifica que el backend esté ejecutándose.';
      }
      
      showToast(errorMsg, 'error', 8000);
    }
  };

  const editarHotspot = (hotspot) => {
    console.log('📝 Editando hotspot completo:', hotspot);
    console.log('📝 Teléfono del hotspot:', hotspot.telefono, 'Tipo:', typeof hotspot.telefono);
    console.log('📝 Link del hotspot:', hotspot.link, 'Tipo:', typeof hotspot.link);
    
    // Manejar valores null, undefined o vacíos correctamente
    const telefonoInicial = (hotspot.telefono !== null && hotspot.telefono !== undefined && hotspot.telefono.toString().trim() !== '') 
      ? hotspot.telefono.toString().trim() 
      : '';
    const linkInicial = (hotspot.link !== null && hotspot.link !== undefined && hotspot.link.toString().trim() !== '') 
      ? hotspot.link.toString().trim() 
      : '';
    
    const hotspotEditado = {
      id: hotspot.id,
      imagen_id: hotspot.imagen_id,
      yaw: hotspot.yaw,
      pitch: hotspot.pitch,
      nombre: hotspot.nombre || '',
      tipo: hotspot.tipo === 'navegacion' ? 'navegacion' : 'info',
      descripcion: hotspot.descripcion || '',
      imagen_destino_id: hotspot.imagen_destino_id || null,
      url_externa: hotspot.url_externa || null,
      telefono: telefonoInicial,
      link: linkInicial
    };
    
    console.log('📝 Hotspot preparado para edición:', hotspotEditado);
    setHotspotEditando(hotspotEditado);
  };

  const eliminarHotspot = async (hotspotId) => {
    if (!window.confirm('¿Eliminar este punto de interés?')) return;

    try {
      await api.delete(`/hotspots/${hotspotId}`);
      showToast('Punto de interés eliminado exitosamente', 'success');
      await cargarHotspotsImagen(imagenes[imagenActual].id);
    } catch (err) {
      showToast('Error al eliminar punto de interés', 'error');
    }
  };

  const cambiarImagen = (index) => {
    if (index >= 0 && index < imagenes.length) {
      setImagenActual(index);
    }
  };

  if (!recorrido || imagenes.length === 0) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="editar-hotspots-container">
      <div className="editar-hotspots-header">
        <h1>Editar Puntos de Interés - {recorrido.titulo}</h1>
        <div className="header-actions">
          {/* Botón Guardar Cambios - visible cuando estamos en modo edición y no hay formulario abierto */}
          {modoEdicion && !hotspotEditando && (
            <button
              onClick={() => {
                // Guardar cambios y salir del modo edición
                if (pannellumRef.current) {
                  const TEMP_HOTSPOT_ID = 'temp-marker-preview';
                  let attempts = 0;
                  while (attempts < 5) {
                    const config = pannellumRef.current.getConfig();
                    const tempHotspots = config.hotSpots?.filter(hs => 
                      hs.id && hs.id.includes(TEMP_HOTSPOT_ID)
                    ) || [];
                    if (tempHotspots.length === 0) break;
                    tempHotspots.forEach(hs => {
                      try {
                        pannellumRef.current.removeHotSpot(hs.id);
                      } catch (e) {
                        const index = config.hotSpots.indexOf(hs);
                        if (index !== -1) {
                          try {
                            pannellumRef.current.removeHotSpot(index);
                          } catch (e2) {}
                        }
                      }
                    });
                    attempts++;
                  }
                  marcadorTemporalRef.current = null;
                }
                setModoEdicion(false);
                showToast('Cambios guardados', 'success');
              }}
              className="btn btn-success"
            >
              Guardar Cambios
            </button>
          )}
          
          {/* Botón Ver Recorrido - siempre visible */}
          {recorrido && (
            <button
              onClick={() => {
                // Navegar al visor del recorrido usando la ruta correcta
                const codigo = recorrido?.codigo;
                if (codigo) {
                  navigate(`/visor/${codigo}`);
                } else if (id) {
                  navigate(`/visor/id/${id}`);
                } else {
                  showToast('No se pudo obtener el código del recorrido', 'error');
                }
              }}
              className="btn btn-info"
            >
              Ver Recorrido
            </button>
          )}
          <button
            onClick={() => {
              const nuevoModo = !modoEdicion;
              if (!nuevoModo) {
                // Al desactivar modo edición, remover todos los marcadores temporales
                if (pannellumRef.current) {
                  const TEMP_HOTSPOT_ID = 'temp-marker-preview';
                  let attempts = 0;
                  while (attempts < 5) {
                    const config = pannellumRef.current.getConfig();
                    const tempHotspots = config.hotSpots?.filter(hs => 
                      hs.id && hs.id.includes(TEMP_HOTSPOT_ID)
                    ) || [];
                    if (tempHotspots.length === 0) break;
                    tempHotspots.forEach(hs => {
                      try {
                        pannellumRef.current.removeHotSpot(hs.id);
                      } catch (e) {
                        const index = config.hotSpots.indexOf(hs);
                        if (index !== -1) {
                          try {
                            pannellumRef.current.removeHotSpot(index);
                          } catch (e2) {}
                        }
                      }
                    });
                    attempts++;
                  }
                  marcadorTemporalRef.current = null;
                }
              }
              setModoEdicion(nuevoModo);
            }}
            className={`btn ${modoEdicion ? 'btn-danger' : 'btn-primary'}`}
          >
            {modoEdicion ? 'Cancelar Edición' : 'Agregar Puntos'}
          </button>
          <button onClick={() => navigate(`/editar-recorrido/${id}`)} className="btn">
            Volver a Editar Recorrido
          </button>
        </div>
      </div>

      <div className="editar-hotspots-content">
        <div className="visor-editor-wrapper">
          <div ref={viewerRef} className="panorama-viewer-editor"></div>
          {/* Marcador temporal visual que sigue el mouse */}
          {modoEdicion && marcadorTemporalPosicion.visible && (
            <div 
              className="hotspot-temporal-preview-html"
              style={{
                position: 'fixed',
                left: `${marcadorTemporalPosicion.x}px`,
                top: `${marcadorTemporalPosicion.y}px`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 2000
              }}
            />
          )}
          {modoEdicion && (
            <div className="modo-edicion-overlay">
              <p>Modo Edición: Haz clic en la imagen para agregar un punto de interés</p>
            </div>
          )}
        </div>

        <div className="hotspots-panel">
          <h2>Imagen {imagenActual + 1} de {imagenes.length}</h2>
          
          <div className="imagenes-selector">
            {imagenes.map((img, index) => (
              <div
                key={img.id}
                className={`imagen-mini ${index === imagenActual ? 'activa' : ''}`}
                onClick={() => cambiarImagen(index)}
              >
                <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${img.ruta_archivo}`} alt={`Vista ${index + 1}`} />
                <span>{index + 1}</span>
              </div>
            ))}
          </div>

          <div className="hotspots-list">
            <h3>Puntos de Interés ({hotspots.length})</h3>
            {hotspots.length === 0 ? (
              <p className="sin-hotspots">No hay puntos de interés en esta imagen</p>
            ) : (
              hotspots.map((hotspot) => (
                <div key={hotspot.id} className="hotspot-item">
                  <div className="hotspot-info">
                    <strong>{hotspot.nombre}</strong>
                    <span className="hotspot-tipo">{hotspot.tipo === 'navegacion' ? 'Navegación' : 'Informativo'}</span>
                    {hotspot.imagen_destino_id && (
                      <span className="hotspot-destino">
                        → Imagen {imagenes.findIndex(img => img.id === hotspot.imagen_destino_id) + 1}
                      </span>
                    )}
                  </div>
                  <div className="hotspot-actions">
                    <button
                      onClick={() => editarHotspot(hotspot)}
                      className="btn btn-primary btn-sm"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => eliminarHotspot(hotspot.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {hotspotEditando && (
        <div className="modal-hotspot" onClick={(e) => {
          // Cerrar modal si se hace clic fuera del contenido
          if (e.target === e.currentTarget) {
            setHotspotEditando(null);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{hotspotEditando.id ? 'Editar Punto de Interés' : 'Agregar Punto de Interés'}</h3>
            <div className="form-group">
              <label>Nombre del Punto *</label>
              <input
                type="text"
                value={hotspotEditando.nombre || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  setHotspotEditando({...hotspotEditando, nombre: e.target.value});
                }}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Ej: Entrada principal"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Tipo</label>
              <select
                value={hotspotEditando.tipo}
                onChange={(e) => {
                  e.stopPropagation();
                  const nuevoTipo = e.target.value;
                  setHotspotEditando({
                    ...hotspotEditando, 
                    tipo: nuevoTipo,
                    // Limpiar imagen_destino_id si cambia a informativo
                    imagen_destino_id: nuevoTipo === 'navegacion' ? hotspotEditando.imagen_destino_id : null
                  });
                }}
                onFocus={(e) => e.stopPropagation()}
              >
                <option value="info">Informativo (mostrar información)</option>
                <option value="navegacion">Navegación (ir a otra imagen)</option>
              </select>
            </div>
            {hotspotEditando.tipo === 'navegacion' && (
              <div className="form-group">
                <label>Ir a Imagen</label>
                <select
                  value={hotspotEditando.imagen_destino_id || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    setHotspotEditando({...hotspotEditando, imagen_destino_id: parseInt(e.target.value) || null});
                  }}
                  onFocus={(e) => e.stopPropagation()}
                >
                  <option value="">Selecciona una imagen</option>
                  {imagenes.map((img, index) => (
                    index !== imagenActual && (
                      <option key={img.id} value={img.id}>
                        Imagen {index + 1} {img.nombre ? `- ${img.nombre}` : ''}
                      </option>
                    )
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <textarea
                value={hotspotEditando.descripcion || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  setHotspotEditando({...hotspotEditando, descripcion: e.target.value});
                }}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Descripción del punto de interés"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Teléfono (opcional)</label>
              <input
                type="tel"
                value={hotspotEditando.telefono || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  setHotspotEditando({...hotspotEditando, telefono: e.target.value});
                }}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Ej: +34 123 456 789"
              />
            </div>
            <div className="form-group">
              <label>Link/URL (opcional)</label>
              <input
                type="url"
                value={hotspotEditando.link || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  setHotspotEditando({...hotspotEditando, link: e.target.value});
                }}
                onFocus={(e) => e.stopPropagation()}
                placeholder="Ej: https://ejemplo.com"
              />
            </div>
            <div className="modal-actions">
              <button 
                onClick={async (e) => {
                  e.stopPropagation();
                  await guardarHotspot();
                }} 
                className="btn btn-primary"
              >
                {hotspotEditando.id ? 'Actualizar Punto' : 'Guardar Punto'}
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  // Remover marcador temporal al cancelar
                  if (marcadorTemporalRef.current !== null && pannellumRef.current) {
                    try {
                      const TEMP_HOTSPOT_ID = 'temp-marker-preview';
                      pannellumRef.current.removeHotSpot(TEMP_HOTSPOT_ID);
                    } catch (err) {
                      if (marcadorTemporalRef.current.index !== undefined) {
                        try {
                          pannellumRef.current.removeHotSpot(marcadorTemporalRef.current.index);
                        } catch (e) {}
                      }
                    }
                    marcadorTemporalRef.current = null;
                  }
                  setHotspotEditando(null);
                }} 
                className="btn"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para mostrar información del hotspot */}
      {hotspotMostrando && (
        <>
          {/* Overlay transparente para cerrar al hacer clic fuera */}
          <div 
            className="modal-hotspot-overlay" 
            onClick={() => {
              console.log('Cerrando modal desde overlay');
              setHotspotMostrando(null);
            }}
            style={{ zIndex: 9999 }}
          />
          {/* Modal de información posicionado cerca del hotspot */}
          <div 
            className="modal-content modal-hotspot-info" 
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: `${Math.min(Math.max(hotspotPosicion.x, 150), window.innerWidth - 150)}px`,
              top: `${Math.min(Math.max(hotspotPosicion.y + 30, 20), window.innerHeight - 200)}px`,
              transform: 'translateX(-50%)',
              maxWidth: '300px',
              zIndex: 10001,
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              margin: '0'
            }}
          >
            <h3>{hotspotMostrando.nombre || 'Punto de Interés'}</h3>
            {console.log('Renderizando modal con hotspot:', hotspotMostrando)}
            {hotspotMostrando.descripcion && hotspotMostrando.descripcion.trim() && (
              <div className="hotspot-descripcion-modal">
                <p>{hotspotMostrando.descripcion}</p>
              </div>
            )}
            {(!hotspotMostrando.descripcion || !hotspotMostrando.descripcion.trim()) && (
              <div className="hotspot-descripcion-modal">
                <p style={{ color: '#7f8c8d', fontStyle: 'italic' }}>No hay descripción disponible para este punto de interés.</p>
              </div>
            )}
            {hotspotMostrando.telefono && hotspotMostrando.telefono.trim() && (
              <div className="hotspot-info-modal">
                <strong>Teléfono:</strong> 
                <a href={`tel:${hotspotMostrando.telefono}`} style={{ marginLeft: '8px', color: '#007bff' }}>
                  {hotspotMostrando.telefono}
                </a>
              </div>
            )}
            {hotspotMostrando.link && hotspotMostrando.link.trim() && (
              <div className="hotspot-info-modal">
                <strong>Link:</strong> 
                <a 
                  href={hotspotMostrando.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ marginLeft: '8px', color: '#007bff', wordBreak: 'break-all' }}
                >
                  {hotspotMostrando.link}
                </a>
              </div>
            )}
            <div className="hotspot-info-modal">
              <span className="hotspot-tipo">
                {hotspotMostrando.tipo === 'navegacion' || hotspotMostrando.tipo === 'navegación' ? 'Navegación' : 'Informativo'}
              </span>
            </div>
            <div className="modal-actions">
              <button 
                onClick={() => {
                  console.log('Cerrando modal de hotspot');
                  setHotspotMostrando(null);
                }} 
                className="btn btn-primary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EditarHotspots;

